import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

interface SyncConfig {
  ownerUid?: string;
  ingest?: {
    enabled?: boolean;
    folders?: Array<{ path: string; cursor?: string }>;
    lastRunAt?: { seconds: number };
    lastResult?: { ingested?: number; errors?: string[] };
  };
  backup?: {
    enabled?: boolean;
    frequencyDays?: number;
    destFolder?: string;
    lastRunAtMs?: number;
    lastResult?: { files?: number; bytes?: number; errors?: string[] };
  };
}

/**
 * Admin panel for scheduled Dropbox sync:
 *   - Inbound: watched folders that auto-ingest new files into SFP.
 *   - Outbound: scheduled full backup of SFP data to Dropbox.
 * The scheduled Cloud Function runs every 6 hours; this just edits the config
 * doc and offers a "run now" button.
 */
export function DropboxSyncPanel() {
  const { user, role } = useAuth();
  const [cfg, setCfg] = useState<SyncConfig | null>(null);
  const [folders, setFolders] = useState('');
  const [ingestEnabled, setIngestEnabled] = useState(false);
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [freqDays, setFreqDays] = useState(1);
  const [destFolder, setDestFolder] = useState('/SFP Backups');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    return onSnapshot(doc(db, 'dropboxSync', 'config'), (snap) => {
      const data = (snap.data() ?? {}) as SyncConfig;
      setCfg(data);
      if (!loaded) {
        setFolders((data.ingest?.folders ?? []).map((f) => f.path).join('\n'));
        setIngestEnabled(!!data.ingest?.enabled);
        setBackupEnabled(!!data.backup?.enabled);
        setFreqDays(data.backup?.frequencyDays ?? 1);
        setDestFolder(data.backup?.destFolder ?? '/SFP Backups');
        setLoaded(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (role !== 'admin') return null;

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'setDropboxSyncConfig');
      await fn({
        ownerUid: user?.uid,
        ingestEnabled,
        ingestFolders: folders
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        backupEnabled,
        backupFrequencyDays: freqDays,
        backupDestFolder: destFolder.trim() || '/SFP Backups',
      });
      setMsg('Saved. The 6-hourly job will use this; or run it now below.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function runNow(what: 'ingest' | 'backup' | 'both') {
    setBusy(true);
    setMsg(`Running ${what}…`);
    try {
      const fn = httpsCallable<unknown, { ingest?: any; backup?: any; skipped?: string }>(
        functions,
        'runDropboxSyncNow',
      );
      const r = await fn({ what });
      if (r.data.skipped) {
        setMsg(`Skipped: ${r.data.skipped}`);
      } else {
        const parts: string[] = [];
        if (r.data.ingest) parts.push(`ingested ${r.data.ingest.ingested} file(s)`);
        if (r.data.backup) parts.push(`backed up ${r.data.backup.files} file(s)`);
        setMsg(parts.length ? `Done — ${parts.join(', ')}.` : 'Done.');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setBusy(false);
    }
  }

  const lastBackup = cfg?.backup?.lastRunAtMs
    ? new Date(cfg.backup.lastRunAtMs).toLocaleString()
    : 'never';
  const lastIngest = cfg?.ingest?.lastRunAt
    ? new Date(cfg.ingest.lastRunAt.seconds * 1000).toLocaleString()
    : 'never';

  return (
    <>
      {/* Inbound — watched folders */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-stone-800">Ingest from Dropbox folders</h2>
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={ingestEnabled}
              onChange={(e) => setIngestEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          List the Dropbox folder paths to watch — one per line (e.g. <code>/SFP/Documents</code>).
          Every 6 hours SFP pulls any new or changed files into the document pipeline (text extract →
          Claude classify → embed). Already-ingested files are skipped.
        </p>
        <textarea
          value={folders}
          onChange={(e) => setFolders(e.target.value)}
          rows={3}
          placeholder={'/SFP/Documents\n/Family/Statements'}
          className="mt-3 w-full rounded border border-stone-300 px-3 py-2 font-mono text-xs"
        />
        <div className="mt-2 flex items-center gap-2 text-[11px] text-stone-500">
          <span>Last ingest run: {lastIngest}</span>
          {cfg?.ingest?.lastResult && (
            <span>· {cfg.ingest.lastResult.ingested ?? 0} file(s) ingested</span>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => void runNow('ingest')}
            disabled={busy}
            className="rounded bg-[#2F4F3D] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Ingest now
          </button>
        </div>
      </div>

      {/* Outbound — scheduled backup */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-stone-800">Back up SFP data to Dropbox</h2>
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={backupEnabled}
              onChange={(e) => setBackupEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          On a schedule, SFP mirrors every uploaded file plus a JSON dump of the core data
          (documents, bills, accounts, entities, family members, users, layouts, audit) into a dated
          folder in your Dropbox.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Frequency</span>
            <select
              value={freqDays}
              onChange={(e) => setFreqDays(Number(e.target.value))}
              className="rounded border border-stone-300 px-2 py-1.5 text-xs"
            >
              <option value={1}>Daily</option>
              <option value={7}>Weekly</option>
              <option value={30}>Monthly</option>
            </select>
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Destination folder in Dropbox</span>
            <input
              value={destFolder}
              onChange={(e) => setDestFolder(e.target.value)}
              placeholder="/SFP Backups"
              className="rounded border border-stone-300 px-2 py-1.5 font-mono text-xs"
            />
          </label>
        </div>
        <div className="mt-2 text-[11px] text-stone-500">
          Last backup: {lastBackup}
          {cfg?.backup?.lastResult && (
            <> · {cfg.backup.lastResult.files ?? 0} file(s), {bytesHuman(cfg.backup.lastResult.bytes ?? 0)}</>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => void runNow('backup')}
            disabled={busy}
            className="rounded bg-[#1F3357] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Back up now
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Working…' : 'Save sync settings'}
        </button>
        {msg && <span className="text-xs text-stone-600">{msg}</span>}
      </div>
    </>
  );
}

function bytesHuman(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
